'use client';
import React, { useState, useMemo } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import CourseCard from './CourseCard';
import CourseFilters from './CourseFilters';
import EmptyState from '@/components/ui/EmptyState';
import { BookOpen } from 'lucide-react';

export type WingType = 'all' | 'foundations' | 'intermediate' | 'advanced' | 'professional';
export type DifficultyType = 'all' | 'beginner' | 'intermediate' | 'advanced';

export interface Course {
  id: string;
  name: string;
  wing: WingType;
  difficulty: DifficultyType;
  ageRange: string;
  duration: string;
  lessons: number;
  aiTutor: string;
  description: string;
  locked: boolean;
  enrolled: boolean;
  progress: number;
  tags: string[];
}

const allCourses: Course[] = [
  { id: 'cat-001', name: 'Basic Mathematics', wing: 'foundations', difficulty: 'beginner', ageRange: '6–9', duration: '4 weeks', lessons: 18, aiTutor: 'Sunny', description: 'Addition, subtraction, multiplication and division through playful visual exercises and storytelling.', locked: false, enrolled: false, progress: 0, tags: ['Math', 'Numbers', 'Foundations'] },
  { id: 'cat-002', name: 'Reading & Writing Essentials', wing: 'foundations', difficulty: 'beginner', ageRange: '6–9', duration: '5 weeks', lessons: 20, aiTutor: 'Sunny', description: 'Build vocabulary, sentence structure, and reading comprehension through interactive stories.', locked: false, enrolled: true, progress: 55, tags: ['Literacy', 'Writing', 'Foundations'] },
  { id: 'cat-003', name: 'Science Basics', wing: 'foundations', difficulty: 'beginner', ageRange: '6–9', duration: '3 weeks', lessons: 14, aiTutor: 'Sunny', description: 'Discover plants, animals, weather, and the human body through curiosity-driven experiments.', locked: false, enrolled: false, progress: 0, tags: ['Science', 'Nature', 'Foundations'] },
  { id: 'cat-004', name: 'Emotional Intelligence', wing: 'foundations', difficulty: 'beginner', ageRange: '6–9', duration: '3 weeks', lessons: 12, aiTutor: 'Sunny', description: 'Understand feelings, empathy, and healthy communication through guided storytelling and reflection.', locked: false, enrolled: false, progress: 0, tags: ['EQ', 'Social Skills', 'Foundations'] },
  { id: 'cat-005', name: 'Creative Arts & Expression', wing: 'foundations', difficulty: 'beginner', ageRange: '6–9', duration: '4 weeks', lessons: 16, aiTutor: 'Sunny', description: 'Explore drawing, music, and storytelling as tools for creative thinking and self-expression.', locked: false, enrolled: false, progress: 0, tags: ['Arts', 'Creativity', 'Foundations'] },
  { id: 'cat-006', name: 'Logic & Problem Solving', wing: 'intermediate', difficulty: 'intermediate', ageRange: '10–13', duration: '5 weeks', lessons: 22, aiTutor: 'Nova', description: 'Sharpen your reasoning skills through puzzles, riddles, and structured logical thinking challenges.', locked: false, enrolled: true, progress: 91, tags: ['Logic', 'Reasoning', 'Intermediate'] },
  { id: 'cat-007', name: 'Intro to Coding', wing: 'intermediate', difficulty: 'intermediate', ageRange: '10–13', duration: '6 weeks', lessons: 16, aiTutor: 'Nova', description: 'Learn Python fundamentals — variables, loops, functions, and your first real programs.', locked: false, enrolled: true, progress: 68, tags: ['Python', 'Coding', 'Intermediate'] },
  { id: 'cat-008', name: 'Earth & Space Science', wing: 'intermediate', difficulty: 'intermediate', ageRange: '10–13', duration: '5 weeks', lessons: 17, aiTutor: 'Nova', description: 'Journey through the solar system, tectonic plates, weather systems, and Earth\'s ecosystems.', locked: false, enrolled: true, progress: 42, tags: ['Space', 'Earth', 'Intermediate'] },
  { id: 'cat-009', name: 'Digital Citizenship', wing: 'intermediate', difficulty: 'intermediate', ageRange: '10–13', duration: '3 weeks', lessons: 10, aiTutor: 'Nova', description: 'Navigate the internet safely, understand privacy, and become a responsible digital community member.', locked: false, enrolled: false, progress: 0, tags: ['Safety', 'Internet', 'Intermediate'] },
  { id: 'cat-010', name: 'Advanced Mathematics', wing: 'advanced', difficulty: 'advanced', ageRange: '14–17', duration: '8 weeks', lessons: 22, aiTutor: 'Orion', description: 'Algebra, trigonometry, calculus fundamentals, and proof-based reasoning for advanced learners.', locked: true, enrolled: false, progress: 0, tags: ['Calculus', 'Algebra', 'Advanced'] },
  { id: 'cat-011', name: 'Physics & Engineering', wing: 'advanced', difficulty: 'advanced', ageRange: '14–17', duration: '7 weeks', lessons: 20, aiTutor: 'Orion', description: 'From Newtonian mechanics to electromagnetism — with engineering design challenges woven throughout.', locked: true, enrolled: false, progress: 0, tags: ['Physics', 'Engineering', 'Advanced'] },
  { id: 'cat-012', name: 'AI Ethics & Safety', wing: 'advanced', difficulty: 'advanced', ageRange: '14–17', duration: '4 weeks', lessons: 14, aiTutor: 'Orion', description: 'Explore how AI systems work, their societal impact, bias detection, and responsible development principles.', locked: true, enrolled: false, progress: 0, tags: ['AI', 'Ethics', 'Advanced'] },
  { id: 'cat-013', name: 'Space Systems', wing: 'advanced', difficulty: 'advanced', ageRange: '14–17', duration: '6 weeks', lessons: 18, aiTutor: 'Orion', description: 'Orbital mechanics, satellite systems, rocket propulsion, and mission design fundamentals.', locked: true, enrolled: false, progress: 0, tags: ['Space', 'Rockets', 'Advanced'] },
  { id: 'cat-014', name: 'Cybersecurity Basics', wing: 'advanced', difficulty: 'advanced', ageRange: '14–17', duration: '5 weeks', lessons: 16, aiTutor: 'Orion', description: 'Network security, cryptography, ethical hacking fundamentals, and digital threat analysis.', locked: true, enrolled: false, progress: 0, tags: ['Security', 'Networks', 'Advanced'] },
  { id: 'cat-015', name: 'Aerospace Systems', wing: 'professional', difficulty: 'advanced', ageRange: '18+', duration: '10 weeks', lessons: 30, aiTutor: 'Atlas', description: 'Complete aerospace engineering curriculum — aerodynamics, propulsion, guidance systems, and mission ops.', locked: false, enrolled: false, progress: 0, tags: ['Aerospace', 'Engineering', 'Professional'] },
  { id: 'cat-016', name: 'AI Development', wing: 'professional', difficulty: 'advanced', ageRange: '18+', duration: '12 weeks', lessons: 36, aiTutor: 'Atlas', description: 'Machine learning, neural networks, LLM architecture, and deployment pipelines with real case studies.', locked: false, enrolled: false, progress: 0, tags: ['ML', 'Deep Learning', 'Professional'] },
  { id: 'cat-017', name: 'Medical Foundations', wing: 'professional', difficulty: 'advanced', ageRange: '18+', duration: '10 weeks', lessons: 28, aiTutor: 'Atlas', description: 'Human anatomy, physiology, pharmacology basics, and clinical reasoning for healthcare professionals.', locked: false, enrolled: false, progress: 0, tags: ['Medicine', 'Anatomy', 'Professional'] },
  { id: 'cat-018', name: 'Leadership & Management', wing: 'professional', difficulty: 'advanced', ageRange: '18+', duration: '6 weeks', lessons: 18, aiTutor: 'Atlas', description: 'Organizational behavior, strategic leadership, decision frameworks, and team dynamics for executives.', locked: false, enrolled: false, progress: 0, tags: ['Leadership', 'Strategy', 'Professional'] },
];

export default function CourseCatalogContent() {
  const [activeWing, setActiveWing] = useState<WingType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [difficulty, setDifficulty] = useState<DifficultyType>('all');
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    return allCourses.filter((c) => {
      const matchesWing = activeWing === 'all' || c.wing === activeWing;
      const matchesDiff = difficulty === 'all' || c.difficulty === difficulty;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.tags.some((t) => t.toLowerCase().includes(q));
      return matchesWing && matchesDiff && matchesSearch;
    });
  }, [activeWing, searchQuery, difficulty]);

  const wingTabs: { id: WingType; label: string; count: number; color: string }[] = [
    { id: 'all', label: 'All Courses', count: allCourses.length, color: 'text-foreground' },
    { id: 'foundations', label: '🌱 Foundations', count: allCourses.filter((c) => c.wing === 'foundations').length, color: 'text-emerald-600' },
    { id: 'intermediate', label: '🌿 Intermediate', count: allCourses.filter((c) => c.wing === 'intermediate').length, color: 'text-blue-600' },
    { id: 'advanced', label: '🌳 Advanced', count: allCourses.filter((c) => c.wing === 'advanced').length, color: 'text-purple-600' },
    { id: 'professional', label: '🌌 Professional', count: allCourses.filter((c) => c.wing === 'professional').length, color: 'text-amber-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground">Course Catalog</h1>
          <p className="text-sm text-muted-foreground mt-1">{allCourses.length} courses across 4 campus wings</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-input border border-border rounded-lg px-3 py-2">
            <Search size={15} className="text-muted-foreground shrink-0" />
            <input
              type="text"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-48"
            />
          </div>
          <button
            onClick={() => setShowFilters((p) => !p)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${showFilters ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted'}`}
          >
            <SlidersHorizontal size={15} />
            Filters
          </button>
        </div>
      </div>

      {/* Wing tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {wingTabs.map((tab) => (
          <button
            key={`wing-tab-${tab.id}`}
            onClick={() => setActiveWing(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-150 shrink-0 ${
              activeWing === tab.id
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeWing === tab.id ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Filters panel */}
      {showFilters && (
        <CourseFilters difficulty={difficulty} onDifficultyChange={setDifficulty} />
      )}

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filtered.length}</span> courses
          {searchQuery && <span> for &ldquo;<span className="text-primary">{searchQuery}</span>&rdquo;</span>}
        </p>
      </div>

      {/* Course grid */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No courses found"
          description="Try adjusting your search or filters to find courses that match what you're looking for."
          action={{ label: 'Clear Filters', onClick: () => { setSearchQuery(''); setActiveWing('all'); setDifficulty('all'); } }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
          {filtered.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      )}
    </div>
  );
}